import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { message } from "antd";
import Header from '../Shared/Header/Header';
import Footer from '../Shared/Footer/Footer';
import ProfileNavigateLayout from './ProfileNavigateLayout';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const NAME_REGEX = /^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]+(?:[-\s][a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ]+)*$/;
const PHONENUMBER_REGEX = /^(\+48[-\s.]?)?(\d{3}[-\s.]?\d{3}[-\s.]?\d{3})$/;


const EditUserAppointmentData = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { item } = location.state || {};
    const { register, handleSubmit, getValues, setValue } = useForm();

    const [isLoading, setIsLoading] = useState(false);
    const [isPhoneNumberChecking, setIsPhoneNumberChecking] = useState(false);
    const [initialData, setInitialData] = useState({});
    const [isFormChanged, setIsFormChanged] = useState(false);
    
    const [errors, setErrors] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phoneNumber: "",
    });
        

    const formatDate = (dateString) => {
        const date = new Date(dateString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure two digits
        const day = String(date.getDate()).padStart(2, "0"); // Ensure two digits
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${day}.${month}.${year}, ${hours}:${minutes}`; // Return the formatted date
    };


    const validateForm = async (formData) => {
        let newErrors = {};
        let isValid = true;

        const phoneNumber = formData?.phoneNumber || getValues("phoneNumber");
        const email = item.user.email;
    
        if (!NAME_REGEX.test(formData.city)) {
            newErrors.city = "Miasto nie może zawierać cyfr ani znaków specjalnych";
            isValid = false;
        }
    
        if (!PHONENUMBER_REGEX.test(formData.phoneNumber)) {
            newErrors.phoneNumber = "Numer telefonu nie ma poprawnego formatu";
            isValid = false;
        } else {
            setIsPhoneNumberChecking(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/user/checkPhoneNumber?phoneNumber=${encodeURIComponent(phoneNumber)}`,
            );
            if (response.ok) {
                const users = await response.json();
                const phoneNumberDoesNotExist = users.some(
                    (user) => user.phoneNumber === phoneNumber,
                );
                if (phoneNumberDoesNotExist) {
                    newErrors.phoneNumber =
                        "Konto z podanym numerem telefonu już istnieje.";
                    isValid = false;
                }
            } else {
                console.error("Błąd przy sprawdzaniu numeru telefonu");
            }
        } catch (error) {
          console.error(
            "Wystąpił błąd podczas sprawdzania numeru telefonu:",
            error,
          );
        } finally {
                setIsPhoneNumberChecking(false);
            }
        }
    
        setErrors(newErrors);
        return isValid;
    };


    useEffect(() => {
        if (item) {
            const appointmentData = {
                phoneNumber: item.user.phoneNumber || "",
                country: item.address.country || "",
                state: item.address.state || "",
                city: item.address.city || "",
                zipCode: item.address.zipCode || "",
                street: item.address.street || "",
                addressNo1: item.address.addressNo1 || "",
                addressNo2: item.address.addressNo2 || ""
            };
            setInitialData(appointmentData);
            Object.keys(appointmentData).forEach(key => setValue(key, appointmentData[key]));
        }
    }, [item, setValue]);


    const findOrCreateAddress = async (addressRequest) => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/address/find-or-create`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(addressRequest),
          });
      
          if (!response.ok) {
            throw new Error("Failed to find or create address");
          }
      
          return await response.json();
        } catch (error) {
          console.error("Error:", error);
          throw error;
        }
    };


    const onSubmit = async (formData) => {
        setIsLoading(true);

        const isValid = await validateForm(formData);

        if (!isValid) {
            setIsLoading(false);
            return;
        }

        const updatedFields = {};
        Object.keys(formData).forEach((key) => {
            if (formData[key] !== initialData[key]) {
                updatedFields[key] = formData[key];
            }
        });
    
        if (Object.keys(updatedFields).length === 0) {
            setIsLoading(false);
            return;
        }

        try {
            // update address data if any of its fields have been changed 
            const addressFields = ["country", "state", "city", "zipCode", "street", "addressNo1", "addressNo2"];
            const addressChanged = addressFields.some(
                (key) => formData[key] !== initialData[key]
            );
    
            
            let addressRequest = null;
            let newAddress = null;
    
            if (addressChanged) {
                addressRequest = {
                    country: formData.country,
                    state: formData.state,
                    city: formData.city,
                    zipCode: formData.zipCode,
                    street: formData.street,
                    addressNo1: formData.addressNo1,
                    addressNo2: formData.addressNo2
                };
    
                newAddress = await findOrCreateAddress(addressRequest);
            }


            //update address if it's changed by user 
            if (newAddress && newAddress.id !== item.address.id) {
                const updateAddressResponse = await fetch(
                    `${API_BASE_URL}/api/appointment/${item.id}/address`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                        body: JSON.stringify(addressRequest),
                    }
                );
        
                if (updateAddressResponse.ok) {
                    message.success("Adres wizyty został zaktualizowany.");
                } else {
                    message.error("Błąd podczas aktualizacji adresu wizyty.");
                }
            }

            // update user if phoneNumber has been changed
            if (updatedFields.phoneNumber) {
                const user = item.user;

                const response = await fetch(`${API_BASE_URL}/api/user/${user.id}`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    body: JSON.stringify({ phoneNumber: updatedFields.phoneNumber }),
                });

                if (response.ok) {
                    message.success("Profil zaktualizowany!");
                } else {
                    message.error("Błąd podczas zapisu zmian.");
                }

                setInitialData({ ...initialData, ...updatedFields });
                setIsFormChanged(false);

                setIsLoading(false);

                navigate('/twoje-wizyty');
            }
        } catch (error) {
            console.error("Error during address/user update:", error);
        } finally {
            setIsLoading(false);
        }

    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setIsFormChanged(value !== initialData[name]);
        setValue(name, value);
    };

    const handleCancel = () => {
        navigate('/twoje-wizyty');
    };


    return (
        <ProfileNavigateLayout>
        <div style={{ marginBottom: '5rem', marginTop: '1rem' }}>
            <div className="w-100 mb-3 rounded mb-5 p-2" style={{ background: '#f8f9fa' }}>
                <h5 className="edit-userdata-header mb-2 mt-3"> 
                    Edycja Wizyty - <span style={{fontWeight: 600, textDecoration: 'underline'}}>{formatDate(item?.dateTime)}</span>
                </h5>
                <form className="row form-row" onSubmit={handleSubmit(onSubmit)}>
                    {/* <div className="col-md-12">
                        <div className="form-group">
                            <div className='change-avatar d-flex gap-2 align-items-center'>
                            </div>
                        </div>
                    </div> */}
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Imię</label>
                            <input defaultValue={item?.user?.firstName} disabled className="form-control" />
                            </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Nazwisko</label>
                            <input defaultValue={item?.user?.lastName} disabled className="form-control" />
                            </div>                           
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Email</label>
                            <input defaultValue={item?.user?.email} disabled className="form-control" />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Numer Telefonu</label>
                            <input defaultValue={item?.user?.phoneNumber} {...register("phoneNumber")} className="form-control" onChange={handleInputChange} />
                        </div>
                        {errors.phoneNumber && (
                            <small className="error-text">{errors.phoneNumber}</small>
                        )}                            
                    </div>

                    <div className="col-md-6" style={{ marginTop:"2rem" }}>
                        <div className="form-group mb-2 card-label">
                            <label>Państwo</label>
                            <input defaultValue={item?.address?.country} {...register("country")} className="form-control" onChange={handleInputChange} />

                        </div>
                    </div>
                    <div className="col-md-6" style={{ marginTop:"2rem" }}>
                        <div className="form-group mb-2 card-label">
                            <label>Województwo</label>
                            <input defaultValue={item?.address?.state} {...register("state")} className="form-control" onChange={handleInputChange} />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Miasto</label>
                            <input defaultValue={item?.address?.city} {...register("city")} className="form-control" onChange={handleInputChange} />
                            </div>
                    </div>        
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Kod Pocztowy</label>
                            <input defaultValue={item?.address?.zipCode} {...register("zipCode")} className="form-control" onChange={handleInputChange} />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Ulica</label>
                            <input defaultValue={item?.address?.street} {...register("street")} className="form-control" onChange={handleInputChange} />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-2 card-label">
                            <label>Numer domu/lokalu</label>
                            <input defaultValue={item?.address?.addressNo1} {...register("addressNo1")} className="form-control" onChange={handleInputChange} />
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <button
                            type="button"
                            className="btn btn-outline-secondary iBtn"
                            onClick={handleCancel} // <-- Replace with your cancel logic
                        >
                            Anuluj
                        </button>
                        <button 
                            type="submit" 
                            className={`btn btn-primary btn-block mt-2 iBtn ${isFormChanged ? 'iBtn-dark' : ''}`} 
                            disabled={!isFormChanged || isLoading}
                            onClick={handleSubmit(onSubmit)}
                        >
                            {isLoading ? 'Ładowanie..' : 'Zapisz Zmiany'}
                        </button>
                    </div>

                </form>
         
          {/* <p> {item} </p> */}
          {/* Render item details */}
        </div>
        </div>
        </ProfileNavigateLayout>
    )

};

export default EditUserAppointmentData;
